/**
 * ActionsLibraryTab — the "Reviews | Actions" sub-tab (Feb 2026 · sprint 374).
 *
 * Reads /api/review-actions with filter params. Admin sees full tenant;
 * Manager sees only outlet-scoped actions per backend enforcement. Row
 * click opens ActionDetailDialog with full carry-forward update history.
 */
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { toast } from 'sonner';
import { AlertTriangle, Search, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ActionDetailModal } from './ActionDetailModal';

const STATUS_LABELS = { open: 'Open', in_progress: 'In Progress', done: 'Done', dropped: 'Dropped' };

function _overdueOf(a) {
  if (!a.due_date || a.status === 'done' || a.status === 'dropped') return false;
  return a.due_date < new Date().toISOString().slice(0, 10);
}

// Local inline dialog removed — use shared ActionDetailModal instead (sprint 375).

export function ActionsLibraryTab({ viewerMode = 'admin' }) {
  const { API, getHeaders } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [statusF, setStatusF] = useState('');
  const [ownerF, setOwnerF] = useState('');
  const [overdueF, setOverdueF] = useState(false);
  const [search, setSearch] = useState('');
  const [detailId, setDetailId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (statusF) p.set('status', statusF);
      if (ownerF) p.set('owner', ownerF);
      if (overdueF) p.set('overdue', 'true');
      if (search.trim()) p.set('search', search.trim());
      const r = await fetch(`${API}/review-actions?${p.toString()}`, { headers: getHeaders() });
      if (r.ok) setItems((await r.json()).items || []);
    } catch { toast.error('Failed to load actions'); }
    finally { setLoading(false); }
  }, [API, getHeaders, statusF, ownerF, overdueF, search]);
  useEffect(() => { load(); }, [load]);

  const jumpToReview = (rid) => navigate((viewerMode === 'manager' ? '/manager' : '/admin') + `/reviews/${rid}`);

  return (
    <div data-testid="actions-library">
      <div className="flex flex-wrap items-end gap-3 mb-4 p-3 bg-white border border-[#E7EBE6] rounded-md">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#7A8B82]">Status</Label>
          <Select value={statusF || 'all'} onValueChange={(v) => setStatusF(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-36 mt-1" data-testid="al-filter-status"><SelectValue placeholder="All"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="done">Done</SelectItem>
              <SelectItem value="dropped">Dropped</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#7A8B82]">Owner</Label>
          <Input value={ownerF} onChange={(e) => setOwnerF(e.target.value)}
                 placeholder="e.g. Purchase" data-testid="al-filter-owner"
                 className="w-44 mt-1"/>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-wider text-[#7A8B82]">Search</Label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-[#7A8B82]"/>
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search task…" className="pl-8" data-testid="al-search"/>
          </div>
        </div>
        <label className="text-xs text-[#5C6B64] flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={overdueF} onChange={(e) => setOverdueF(e.target.checked)}
                 data-testid="al-filter-overdue"/>
          Overdue only
        </label>
      </div>

      <div className="border border-[#E7EBE6] rounded-md bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F5F2]">
            <tr className="text-[10px] uppercase tracking-widest text-[#7A8B82]">
              <th className="text-left px-3 py-2 font-semibold">Action</th>
              <th className="text-left px-3 py-2 font-semibold">Owner</th>
              <th className="text-left px-3 py-2 font-semibold">Due</th>
              <th className="text-left px-3 py-2 font-semibold">Status</th>
              <th className="text-left px-3 py-2 font-semibold">From Review</th>
              <th className="text-left px-3 py-2 font-semibold">Last Update</th>
              <th className="text-right px-3 py-2 font-semibold">Open</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center px-3 py-8 text-[#7A8B82]">Loading…</td></tr>}
            {!loading && !items.length && (
              <tr><td colSpan={7} className="text-center px-3 py-10 text-[#7A8B82]">No actions yet.</td></tr>
            )}
            {items.map(a => {
              const overdue = _overdueOf(a);
              const lastUpdate = (a.update_history || []).slice(-1)[0];
              return (
                <tr key={a.id} className="border-t border-[#E7EBE6] hover:bg-[#F9F9F8]" data-testid={`al-row-${a.id}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => setDetailId(a.id)}
                            data-testid={`al-open-${a.id}`}
                            className="text-[color:var(--color-primary-hover)] font-medium hover:underline text-left">
                      {a.task}
                    </button>
                    {a.context_label && <p className="text-[10px] text-[#7A8B82]">{a.context_label}</p>}
                  </td>
                  <td className="px-3 py-2 text-[#5C6B64] text-xs">{a.owner_text || '—'}</td>
                  <td className={`px-3 py-2 text-xs ${overdue ? 'text-red-600 font-semibold' : 'text-[#5C6B64]'}`}>
                    {a.due_date || '—'}
                    {overdue && <span className="ml-1" data-testid={`al-overdue-${a.id}`}><AlertTriangle className="w-3 h-3 inline"/></span>}
                  </td>
                  <td className="px-3 py-2 text-[10px]">
                    <span className={`uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      a.status === 'done' ? 'bg-emerald-100 text-emerald-700' :
                      a.status === 'dropped' ? 'bg-gray-100 text-gray-600' :
                      a.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'}`}>
                      {STATUS_LABELS[a.status] || a.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[#5C6B64] text-xs">{a.originating_review_title}</td>
                  <td className="px-3 py-2 text-[#5C6B64] text-xs">
                    {lastUpdate
                      ? new Date(lastUpdate.created_at).toLocaleDateString()
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => jumpToReview(a.originating_review_id)}
                            data-testid={`al-jump-${a.id}`}
                            className="p-1 text-[#5C6B64] hover:text-[color:var(--color-primary)]"
                            title="Open originating review">
                      <ExternalLink className="w-3.5 h-3.5"/>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detailId && (
        <ActionDetailModal actionId={detailId}
                             onClose={() => setDetailId(null)}
                             onUpdated={load}/>
      )}
    </div>
  );
}
